import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import message from '../components/Brand/brandMessage';
import { ShareIcon, UsersIcon } from '../components/Brand/Icon';
import { getLocalStorage, getUser } from '../utils/func';
import config from '../utils/config';
import { subscribeSessionChanges } from '../hooks/useSessionIdentity';
import { shareLink } from '../utils/shareLink';
import { postShare } from '../utils/postShare';
import url from '../utils/url';
import appLinks from '../appLinks/config.json';
import { inAppRequest } from './api';
import { isCandidate, isEngagementBlocked } from './eligibility';

const MINUTE = 60000;
const isTip = candidate => candidate.kind !== 'story_milestone';

export default function InAppProvider({ ready, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [, setSessionVersion] = useState(0);
  const token = getLocalStorage(config.token), userId = getUser()?.id;
  const latest = useRef({});
  latest.current = { pathname: location.pathname, token, userId, ready, t, navigate, language: i18n.resolvedLanguage };

  useEffect(() => {
    const changed = () => {
      if (latest.current.token !== getLocalStorage(config.token) || latest.current.userId !== getUser()?.id) {
        setSessionVersion(version => version + 1);
      }
    };
    return subscribeSessionChanges(changed);
  }, []);

  useEffect(() => {
    if (!ready || !token || !userId) return undefined;
    const controller = new AbortController();
    let state = null, statePending = false, claimPending = false, pending = null, current = null;
    let quietUntil = Date.now() + 2000, lastActivity = Date.now(), lastState = 0, lastClaim = 0;
    let path = latest.current.pathname, teachingUsed = false;
    const sameSession = () => !controller.signal.aborted && getLocalStorage(config.token) === token && getUser()?.id === userId;
    const request = (endpoint, options = {}) => inAppRequest(endpoint, { ...options, token, signal: controller.signal });
    const outcome = (candidate, action) => request('/outcome', { method: 'POST', body: { id: candidate.id, action } });
    const refresh = async () => {
      if (!sameSession() || statePending || document.hidden || navigator.onLine === false) return;
      statePending = true; lastState = Date.now();
      try {
        const value = await request('/state');
        if (!sameSession()) return;
        state = value;
        if (!state.enabled || (current && !(isTip(current.candidate) ? state.preferences?.tips : state.preferences?.events))) current?.handle?.close();
      } catch { /* This optional feature never replaces usable content with a failure. */ }
      finally { statePending = false; }
    };
    const present = (candidate, context) => {
      const { t: translate, language } = latest.current;
      const event = candidate.kind === 'story_milestone';
      const title = typeof candidate.storyTitle === 'string' ? candidate.storyTitle
        : candidate.storyId === context?.storyId && typeof context.title === 'string' ? context.title : '';
      const key = event ? candidate.submissionKind === 'matched' ? 'matchedMilestone' : 'storyMilestone' : candidate.kind;
      const content = <>{translate(`inApp:${key}`, { formattedCount: new Intl.NumberFormat(language || 'en').format(candidate.count || 0) })}
        {event && title && <span className="wiw-message-story">{title}</span>}</>;
      let acknowledged = null;
      const shown = () => {
        if (!acknowledged) acknowledged = outcome(candidate, 'shown').then(result => {
          if (result.accepted === false && current?.candidate.id === candidate.id) current.handle?.close();
          return result.accepted !== false;
        }).catch(() => { if (current?.candidate.id === candidate.id) current.handle?.close(); return false; });
        return acknowledged;
      };
      const onClose = reason => {
        if (current?.candidate.id === candidate.id) current = null;
        const intentional = reason === 'acted' || reason === 'dismissed';
        // A quick deliberate action proves visibility even before the one-second
        // observer. Preserve order while the share itself stays in the tap stack.
        const acknowledgedClose = intentional ? shown() : acknowledged;
        void Promise.resolve(acknowledgedClose).then(accepted => outcome(candidate,
          intentional && accepted ? reason : 'expired')).catch(() => {});
      };
      const action = event ? {
        label: translate('inApp:view'), icon: <UsersIcon />,
        onClick: () => latest.current.navigate(url(`/news/${candidate.storyId}/reactions`)),
      } : {
        label: translate('inApp:share'), icon: <ShareIcon />,
        onClick: () => {
          const target = candidate.kind === 'publication' && candidate.reactionId
            ? { type: 'opinion', id: candidate.reactionId, path: `/opinion/${candidate.reactionId}` }
            : { type: 'story', id: candidate.storyId, path: `/news/${candidate.storyId}/statistic` };
          const opinionShare = target.type === 'opinion' ? postShare({ id: target.id, story: { title }, user: getUser() }) : null;
          const data = opinionShare ? { title: opinionShare.shareTitle, text: opinionShare.shareText, url: opinionShare.shareUrl }
            : { title: title || 'WIW', text: title || 'WIW', url: new URL(url(target.path), appLinks.origin).href };
          // No await before this call: Safari requires the original user gesture.
          void shareLink(data,
            { source: 'in_app_tip', targetType: target.type, targetId: target.id, notificationId: candidate.id })
            .then(result => { if (sameSession() && result === 'copied') message.success(translate('brand:shareCopied')); })
            .catch(error => { if (sameSession()) message.error(translate(error?.code === 'SHARE_COPY_FAILED' ? 'brand:shareCopyError' : 'brand:shareError')); });
        },
      };
      const handle = message.info({ content, action, duration: 8, engagement: true,
        closeLabel: translate('inApp:close'), onShown: shown, onClose });
      if (handle) current = { candidate, handle };
      else void outcome(candidate, 'expired').catch(() => {});
    };
    const claim = async () => {
      if (!sameSession() || !state?.enabled || claimPending || current || message.hasActive()
        || isEngagementBlocked(latest.current.pathname, quietUntil)) return;
      if (pending && (Date.now() - pending.at > 120000
        || (pending.kind === 'publication' && !latest.current.pathname.endsWith(`/opinion/${pending.reactionId}`)))) pending = null;
      if (!pending && (Date.now() - lastClaim < MINUTE || Date.now() - lastActivity >= MINUTE)) return;
      const context = pending;
      if (context && (!state.preferences?.tips || teachingUsed)) pending = null;
      const signal = pending;
      pending = null; claimPending = true; lastClaim = Date.now();
      const requestingPath = latest.current.pathname;
      try {
        const result = await request('/claim', { method: 'POST', body: signal
          ? { kind: signal.kind, storyId: signal.storyId, ...(signal.reactionId ? { reactionId: signal.reactionId } : {}) }
          : { kind: 'idle' } });
        if (!sameSession() || !result.candidate) return;
        const candidate = result.candidate;
        if (!isCandidate(candidate)) return;
        if (requestingPath !== latest.current.pathname || isEngagementBlocked(latest.current.pathname, quietUntil)
          || message.hasActive() || (isTip(candidate) && (teachingUsed || !state.preferences?.tips)) || (!isTip(candidate) && !state.preferences?.events)) {
          void outcome(candidate, 'expired').catch(() => {});
          return;
        }
        present(candidate, context);
      } catch { /* Claims are optional and expire safely on the server. */ }
      finally { claimPending = false; }
    };
    const receive = event => {
      const value = event.detail;
      if (!sameSession() || value?.userId !== userId || teachingUsed
        || !['publication', 'statistics', 'respondents'].includes(value.kind)) return;
      pending = value;
    };
    const preferences = event => {
      if (event.detail?.userId !== userId) return;
      state = { ...state, preferences: event.detail.preferences };
      if (current && !(isTip(current.candidate) ? state.preferences?.tips : state.preferences?.events)) current.handle?.close();
    };
    const shared = () => {
      teachingUsed = true; pending = null;
      if (current && isTip(current.candidate)) current.handle?.close();
    };
    const interaction = () => { lastActivity = Date.now(); quietUntil = lastActivity + 1200; };
    const visibility = () => {
      if (document.hidden) { pending = null; current?.handle?.close(); }
      else { lastActivity = Date.now(); quietUntil = lastActivity + 2000; void refresh(); }
    };
    const tick = () => {
      if (!sameSession()) return;
      if (path !== latest.current.pathname) {
        path = latest.current.pathname;
        current?.handle?.close();
        if (pending?.kind !== 'publication') pending = null;
        quietUntil = Date.now() + 2000;
      }
      if (current && isEngagementBlocked(path)) current.handle?.close();
      if (document.hidden || Date.now() - lastActivity >= 30 * MINUTE) return;
      if (Date.now() - lastState >= MINUTE && Date.now() - lastActivity < MINUTE) void refresh();
      void claim();
    };
    window.addEventListener('wiw:in-app-signal', receive);
    window.addEventListener('wiw:in-app-preferences', preferences);
    window.addEventListener('wiw:share-initiated', shared);
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('online', visibility);
    ['pointerdown', 'touchmove', 'wheel', 'keydown', 'scroll'].forEach(type => document.addEventListener(type, interaction, { capture: true, passive: true }));
    void refresh();
    const timer = setInterval(tick, 1000);
    return () => {
      current?.handle?.close();
      controller.abort(); clearInterval(timer);
      window.removeEventListener('wiw:in-app-signal', receive);
      window.removeEventListener('wiw:in-app-preferences', preferences);
      window.removeEventListener('wiw:share-initiated', shared);
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('online', visibility);
      ['pointerdown', 'touchmove', 'wheel', 'keydown', 'scroll'].forEach(type => document.removeEventListener(type, interaction, true));
    };
  }, [ready, token, userId]);
  return children;
}
